<template>
    <div class="col-sm-12 nopads" v-bind:class="{ dynamic_width: dynamic}">
        <div class="d-flex flex-column editor-form"
             style="height: 85vh; border-radius: .5em; overflow-y: hidden !important;" v-bind:class="{full_width: noMargin }" @click.stop="">
            <!-- Header with Street View link -->
            <header class="flex-shrink-0 border-bottom">
                <div class="d-flex">
                    <div class="me-2">
                        <h2 class="editor-header p-1">{{ !assignmentUpdate || assignmentUpdate.id < 1 ?  $t('work_assignment.add') : $t('work_assignment.edit')}}</h2>
                    </div>
                    <div class="flex-grow-1 d-flex justify-content-end align-items-center">
                        <google-street-view-link v-if="workAssignment" :latLon="getWorkAssignmentLatLon(workAssignment)"/>
                        <google-street-view-link v-else-if="coord && coord.length > 0" :latLon="{lat: coord[0].lat, lon: coord[0].lng}"/>
                        <editor-item-browser :index="index" :item-count="itemCount" @nextItem="$emit('nextItem')" @previousItem="$emit('previousItem')"/>
                    </div>
                </div>
            </header>
            <!-- Scrollable content -->
            <main class="flex-grow-1 px-2" style="overflow-y: auto; overflow-x: hidden">
                <draw-geometry-wizard
                    v-if="showDrawGeometryWizard !== false"
                    :savedGeometry="assignmentUpdate.geometry"
                    :type="OTHER"
                    @close="closeDrawGeometryWizard"
                    @addDrawnGeometry="addGeometryToAssignment"
                />
                <!-- Map and photos -->
                <work-assignment-map-photos
                    :work-assignment="assignmentUpdate"
                    :photos="assignmentPhotos"
                />
                <!-- Workassignment geometry -->
                <div v-if="!assignmentUpdate.position && !assignmentUpdate.area_defect && !assignmentUpdate.area_equipment && !assignmentUpdate.observation" class="row">
                    <div class="col-12 d-flex justify-content-end">
                        <button
                            id="removeGeomButton"
                            v-if="userDefinedGeometry"
                            class="btn btn-sm btn-danger form-button"
                            :disabled="loading > 0"
                            @click.stop="removeDrawnGeometry"
                        >
                            {{ $t('orders.remove_geometry') }}
                        </button>
                        <button
                            id="updateGeomButton"
                            v-if="assignmentUpdate.geometry"
                            class="btn btn-sm btn-primary form-button"
                            :disabled="assignmentUpdate.position != null || assignmentUpdate.area_defect != null || assignmentUpdate.area_equipment != null || assignmentUpdate.observation != null"
                            @click.stop="showDrawGeometryWizard = !showDrawGeometryWizard"
                        >
                            {{ $t('map.redefine_geometry') }}
                        </button>
                        <button
                            id="addGeomButton"
                            v-if="!assignmentUpdate.geometry"
                            class="btn btn-sm btn-primary form-button"
                            :disabled="assignmentUpdate.position != null || assignmentUpdate.area_defect != null || assignmentUpdate.area_equipment != null || assignmentUpdate.observation != null"
                            @click.stop="showDrawGeometryWizard = !showDrawGeometryWizard"
                        >
                            {{ $t('map.define_geometry') }}
                        </button>
                    </div>
                </div>

                <div class="col-sm-12">
                    <!-- Observation data -->
                    <work-assignment-observation
                        v-if="observation"
                        :observation="observation"/>
                    <!-- Area defect data -->
                    <work-assignment-area-defect
                        v-if="assignmentUpdate.area_defect"
                        :defect="assignmentUpdate.area_defect"/>
                    <!-- Vehicle defect data -->
                    <work-assignment-vehicle-defect
                        v-if="vehicleDefect"
                        :defect="vehicleDefect"/>
                    <!-- WorkPermission data -->
                    <work-assignment-work-permission
                        v-if="workPermission"
                        :work-permission="workPermission"/>
                    <hr v-if="workPermission || vehicleDefect || assignmentUpdate.area_defect || observation" class="pb-0 mb-0"/>

                    <!-- Comments-->
                    <comment-container
                        ref="comments"
                        v-if="assignmentUpdate && assignmentUpdate.id > 0"
                        :topic="commentTopics.TOPIC_WORK_ASSIGNMENT"
                        :id="assignmentUpdate.id"
                        :custom-api-url="workAssignmentCommentUrl"
                        :user="user"
                        @commentsReady="initPhotos"
                    />
                    <hr v-if="assignmentUpdate && assignmentUpdate.id > 0"/>

                    <!-- BASIC INFO -->
                    <h4 class="form-header">{{ $t('work_assignment.basic_info')}}</h4>
                    <div v-if="assignmentUpdate && assignmentUpdate.id > 0" class="row">
                        <div class="col-6">
                            <span class="span-title">{{ $t('work_assignment.id') }}</span>
                            {{ assignmentUpdate?.id }}
                        </div>
                    </div>
                    <div class="row">
                        <div class="row">
                            <div class="col-sm-6">
                                <span class="span-title">{{ $t('work_assignment.select_status') }}</span>
                                <select
                                    id="status"
                                    class="form-select form-select-sm"
                                    v-model="assignmentUpdate.status"
                                    :disabled="assignmentUpdate.id < 1"
                                >
                                    <option
                                        v-for="option in getStatus(assignmentUpdate.id < 1)"
                                        :key="option.key"
                                        :value="option.status"
                                    >
                                        {{ option.key }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-sm-3">
                                <span class="span-title">{{ $t('observation_list.archived') }}</span>
                                <input
                                    class="form-check-input"
                                    v-model="assignmentUpdate.archived"
                                    type="checkbox"
                                >
                                <label for="checkbox" class="ms-2" style="font-size: .9em">{{ $t('work_assignment.archived_hint') }}</label>

                                <!-- Hint user that assignment is still open -->
                                <div v-if="assignmentUpdate.archived && assignmentUpdate.status < 10" class="info-box mt-4 col-12">
                                    <font-awesome-icon icon="info-circle" class="info-circle" />
                                    <span>{{ $t('work_assignment.archiving_open_assignment') }}</span>
                                </div>

                            </div>
                            <div class="col-sm-3">
                                <span class="span-title">{{ $t('work_assignment.select_priority') }}</span>
                                <select
                                    id="priority"
                                    class="form-select form-select-sm"
                                    v-model="assignmentUpdate.priority_value"
                                >
                                    <option
                                        v-for="option in getPriority(assignmentUpdate.id < 1)"
                                        :key="option.key"
                                        :value="option.priorityValue"
                                    >
                                        {{ option.key }}
                                    </option>
                                </select>
                            </div>
                        </div>

                    </div>
                    <div v-if="assignmentUpdate && assignmentUpdate.id > 0" class="row">
                        <div class="col-12 pt-1">
                            <timeline-container :events="assignmentEvents"/>
                        </div>
                    </div>
                    <hr/>

                    <!-- Show open contracts  -->
                    <div class="col-12 nopads">
                        <span class="span-title">{{ $t('orders.contract') }}</span>
                        <multiselect
                            v-model="assignmentUpdate.contract"
                            :options="contracts"
                            :placeholder="$t('orders.select_contract')"
                            label="name"
                            track-by="id"
                            :allow-empty="true"
                            :show-labels="false"
                            class="mb-3"
                        />
                    </div>

                    <div class="col-12 nopads">
                        <contract-order-multiselect
                            ref="orderFilter"
                            :contract-ids="assignmentUpdate.contract ?  [assignmentUpdate.contract.id] : []"
                            :single-mode="true"
                            :pre-selected-order="assignmentUpdate.work_order ? assignmentUpdate.work_order.id : null"
                            :open-or-planned-only="true"
                            @orders-selected="setOrder"
                        />
                    </div>
                    <div class="row">
                        <div class="col-sm-6 nopads pr-1">
                            <span class="span-title">
                              {{ $t('orders.planned_start') }}
                            </span>
                            <input
                                id="start"
                                type="date"
                                class="form-control form-control-sm"
                                :value="timestampToDateInput(assignmentUpdate.planned_start)"
                                @blur="updateStartDate"
                            />
                        </div>
                        <div class="col-sm-6 nopads pr-1">
                            <span class="span-title">
                              {{ $t('orders.planned_ready') }}
                            </span>
                            <input
                                id="end"
                                type="date"
                                class="form-control form-control-sm"
                                :value="timestampToDateInput(assignmentUpdate.planned_ready)"
                                @blur="updateEndDate"
                            />
                        </div>
                    </div>

                    <div class="row" v-if="!assignmentUpdate.area_defect && !assignmentUpdate.geometry">
                        <span class="span-title">{{ $t('work_assignment.area_equipment') }}</span>
                        <area-equipment-selector
                            v-model="assignmentUpdate.area_equipment"
                            :area-id="assignmentUpdate.area_equipment && assignmentUpdate.area_equipment.area ? assignmentUpdate.area_equipment.area.id : undefined"
                        />
                    </div>

                    <div class="row">
                        <div class="col-sm-6 nopads pr-1">
                            <span class="span-title">{{ $t('work_assignment.street') }}</span>
                            <input
                                type="text"
                                id="street"
                                class="form-control form-control-sm"
                                v-model="assignmentUpdate.street_address"
                            />
                        </div>
                        <div class="col-sm-6 nopads pr-1">
                            <span class="span-title">{{ $t('work_assignment.city') }}</span>
                            <input
                                type="text"
                                id="city"
                                class="form-control form-control-sm"
                                v-model="assignmentUpdate.city"
                            />
                        </div>
                    </div>

                    <div>
                        <span class="span-title">{{ $t('users.email') }}</span>
                        <input
                            type="text"
                            id="email"
                            class="form-control form-control-sm"
                            v-model="assignmentUpdate.email"
                        />
                    </div>
                    <div v-if="assignmentUpdate.contract && assignmentUpdate.contract.harja_contract_id" class="nopads">
                        <hr/>
                        <div class="col-12 nopads pb-2">
                            <h4 class="form-header">{{ $t('orders.external_section') }}</h4>
                        </div>
                        <div class="row pb-4">
                            <div class="col-sm-10 col-md-8 col-lg-5">
                                <harja-task-picker :contractId="assignmentUpdate.contract.id"
                                    :assignedTasks="assignmentUpdate.external_tasks" />
                            </div>
                        </div>
                    </div>
                    <hr/>
                    <h4 class="form-header">{{ $t('work_assignment.responsibility')}}</h4>
                    <span class="tip-light">{{ $t('work_assignment.responsibility_tip')}}</span>
                    <div v-if="canAssign">
                        <div v-if="canAssign()">
                            <span class="span-title">{{ $t('work_assignment.select_assignee_company') }}</span>
                            <select
                                id="assigned_company"
                                class="form-select form-select-sm"
                                v-model="assignmentUpdate.assigned_company.id"
                            >
                                <option :value="null">
                                    {{ $t('work_assignment.select_assignee_company') }}
                                </option>
                                <option
                                    v-for="contractor in contractors"
                                    :key="contractor.id"
                                    :value="contractor.id"
                                >
                                    {{ contractor.name }}
                                </option>
                            </select>
                        </div>
                        <div v-if="canAssign()">
                            <span class="span-title">{{ $t('work_assignment.select_assignee') }}</span>
                            <span class="hint-text mb-4" v-if="!assigneeInsideOrganization && assignmentUpdate.assigned_to">{{ $t('work_assignment.outside_org_assignee') }} ({{assignmentUpdate.assigned_to.first_name }} {{assignmentUpdate.assigned_to.last_name }})</span>
                            <select
                                id="assigned_person"
                                class="form-select form-select-sm"
                                v-model="assignee"
                            >
                                <option :value="null">
                                    {{ $t('work_assignment.select_assignee') }}
                                </option>
                                <option
                                    v-for="user in users"
                                    :key="user.id"
                                    :value="user.id"
                                >
                                    {{ user.last_name }}, {{ user.first_name }}
                                </option>
                            </select>
                        </div>
                        <div class="pr-1 mt-1">
                            <span class="span-title">{{ $t('menu.work_time') }}</span>
                            <input
                                type="number"
                                class="form-control form-control-sm"
                                id="worktime"
                                step="0.01"
                                v-model="assignmentUpdate.work_time"
                            />
                        </div>
                    </div>

                    <!-- Additional info -->
                    <hr/>
                    <h4 class="form-header">{{ $t('orders.info')}}</h4>
                    <textarea
                        id="info"
                        class="form-control form-control-sm"
                        v-model="assignmentUpdate.info"
                        rows="3"
                        :style="{ maxHeight: 'calc(1.5em * ' + 6 + ')'}"
                    ></textarea>

                    <!-- Attachments-->
                    <hr/>
                    <h4 class="form-header">{{ $t('orders.attachments_and_info')}}</h4>

                    <div v-if="assignmentUpdate.attachments && assignmentUpdate.attachments.length">
                        <span class="span-title">{{ $t('common.attachments') }}</span>
                        <div
                            v-for="(item, index) in assignmentUpdate.attachments"
                            :key="item.id"
                            class="row border-bottom pb-1"
                            style="font-size: .9em"
                        >
                            <div
                                class="col-10 pointer link pl-0 pt-1 ml-0"
                                @click="downloadAttachment(item)"
                            >
                                {{ item.file_original_name }}
                            </div>
                            <div class="col-2">
                                <button
                                    class="btn btn-danger btn-sm ms-3"
                                    @click.stop="removeOldAttachment(index)"
                                >
                                    <font-awesome-icon icon="trash" />
                                </button>
                            </div>
                        </div>
                    </div>
                    <!-- Attachment upload-->
                    <div class="col-12">
                        <!-- Show selected attachments -->
                        <div
                            v-for="(file, index) in attachments"
                            :key="index"
                            class="d-flex align-items-center justify-content-between mb-3"
                        >
                            <span>{{ file?.name || file }}</span>
                            <button
                                class="btn btn-outline-danger btn-sm"
                                type="button"
                                @click="removeAttachment(index)"
                            >
                                {{ $t('common.remove') }}
                            </button>
                        </div>

                        <file-drop-zone
                            :accept="getValidAttachmentTypes()"
                            class="my-2"
                            @files-selected="files => files.forEach(f => attachments.push(f))"
                        />

                    </div>
                </div>
            </main>
            <!-- Footer -->
            <footer class="flex-shrink-0 border-top">
                <div class="col-sm-12 d-flex justify-content-end py-2">
                    <button
                        id="addButton"
                        class="btn btn-sm btn-primary form-button   "
                        :disabled="!canCreateObservation || loading > 0 || assignmentUpdate.observation !== null ||
                                assignmentUpdate.geometry === null"
                        @click.stop="addObservation"
                    >
                        {{ $t('observations.add_observation') }}
                    </button>

                    <button
                        class="btn btn-sm btn-info form-button"
                        id="addButton"
                        @click.stop="copyWorkAssignment"
                    >
                        {{ $t('common.copy') }}
                    </button>

                    <button
                        id="closeButton"
                        class="btn btn-sm btn-secondary form-button"
                        @click.stop="$emit('close')"
                    >
                        {{ $t('common.cancel') }}
                    </button>
                    <button
                        id="saveButton"
                        class="btn btn-sm btn-success form-button"
                        :disabled="loading  > 0"
                        @click.stop="preSubmit"
                    >
                        {{ $t('common.save') }}
                    </button>
                </div>
            </footer>
        </div>

        <!-- Workassignment copy confirmation -->
        <work-assignment-copy-modal
            v-if="showCopyModal"
            :assignment-update="assignmentUpdate"
            @cancel="cancelCopying"
            @updateSuccess="copySuccess"
        />

        <Transition v-if="observationWizard" name="fade">
            <observation-wizard
                :coordinates="observationCoordinates"
                :skip-photos="true"
                @close="observationWizard = false"
                @closeAndUpdate="observationAdded"
            />
        </Transition>


        <Transition v-if="editObservation" name="fade">
            <observation-editor
                :observation="editableObservation"
                :is-observer="isObserver"
                :old-work-assignment="assignmentUpdate"
                id="editor"
                :allow-assign="false"
                :user="user"
                @scrollTop="scrollTop"
                @close="closeForm"
                @closeAndUpdate="closeAndUpdateObservations"
            />
        </Transition>

        <div
            v-if="loading  > 0"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {geomTypes} from '../mixins/ObservationMixin'
import {workAssignmentHelper} from '../mixins/WorkAssignmentMixin'
import WorkAssignmentObservation from './WorkAssignmentObservation'
import WorkAssignmentAreaDefect from './WorkAssignmentAreaDefect'
import WorkAssignmentVehicleDefect from './WorkAssignmentVehicleDefect'
import WorkAssignmentWorkPermission from './WorkAssignmentWorkPermission'
import {timeUtils} from '../mixins/TimeUtils'
import {attachmentHelper} from "@/components/mixins/AttachmentMixin";
import {geoCoder} from "@/components/mixins/GeocodingMixin";
import AreaEquipmentSelector from "../area/areaequipment/AreaEquipmentSelector";
import DrawGeometryWizard from "../shapeimport/DrawGeometryWizard";
import WorkAssignmentCopyModal from "./WorkAssignmentCopyModal.vue";
import ObservationWizard from "@/components/observation/ObservationWizard";
import HarjaTaskPicker from "@/components/harja/HarjaTaskPicker";
import {mapHelper} from "@/components/mixins/MapMixin";
import {commentTopics} from "@/components/mixins/CommentMixin";
import CommentContainer from "@/components/comments/CommentContainer.vue";
import {inject} from "vue";
import GoogleStreetViewLink from "@/components/utils/GoogleStreetViewLink";
import WorkAssignmentMapPhotos from "./WorkAssignmentMapPhotos.vue";
import {observationHelper} from "../mixins/ObservationMixin";
import EditorItemBrowser from "../common/EditorItemBrowser.vue";
import FileDropZone from '@/components/common/FileDropZone.vue'
import ContractOrderMultiselect from "../order/ContractOrderMultiselect.vue";
import TimelineContainer from "../utils/TimelineContainer.vue";

export default {
    name: 'WorkAssignmentEditor',
    components: {
        TimelineContainer,
        ContractOrderMultiselect,
        EditorItemBrowser,
        WorkAssignmentMapPhotos,
        GoogleStreetViewLink,
        CommentContainer,
        ObservationWizard,
        DrawGeometryWizard,
        AreaEquipmentSelector,
        ObservationEditor: () => import('../observation/ObservationEditor.vue'),
        WorkAssignmentVehicleDefect,
        WorkAssignmentAreaDefect,
        WorkAssignmentObservation,
        WorkAssignmentWorkPermission,
        WorkAssignmentCopyModal,
        HarjaTaskPicker,
        FileDropZone
    },
    mixins: [workAssignmentHelper, restApi, timeUtils, commentTopics,
        attachmentHelper, geoCoder, geomTypes, mapHelper, observationHelper],
    props: {
        workAssignment: {
            type: Object,
            default: null
        },
        coord: {
            type: Array,
            default() {
                return []
            }
        },
        noMargin: {
            type: Boolean,
            default: false
        },
        user: {
            type: Object,
            default: null
        },
        viaObservation: {
            type: Boolean,
            default: false
        },
        observation: null,
        areaDefect: null,
        areaEquipment: null,
        vehicleDefect: null,
        workDefect: null,
        workPermission: null,
        isObserver: Boolean,
        dynamic: Boolean,
        index: null,
        itemCount: null
    },
    data: function () {
        return {
            loading: 0,
            company: null,
            contracts: [],
            assignmentUpdate: this.newWorkAssignment(this.workAssignment),
            editableObservation: null,
            users: [],
            contractors: [],
            observationWizard: false,
            editObservation: false,
            attachments: [],
            addressHasChanged: false,
            showDrawGeometryWizard: false,
            userDefinedGeometry: false,
            showCopyModal: false,
            assignee: -1,
            assignmentPhotos: [],
            assignmentEvents: []
        }
    },

    mounted: function () {
        if (!this.assignmentUpdate.area_equipment && this.areaEquipment) {
            this.assignmentUpdate.area_equipment = this.areaEquipment;
            if (!this.assignmentUpdate.area_equipment.area && this.areaEquipment.area) {
                this.assignmentUpdate.area_equipment.area = this.areaEquipment.area.id
            }
        }
        if (this.workDefect) {
            this.initializeFromWorkDefect()
        }
        if (this.areaDefect) {
            this.assignmentUpdate.area_defect = this.areaDefect
        }
        if (this.observation) {
            this.assignmentUpdate.observation = this.observation
            if(this.observation.contract && this.observation.contract.id &&
                this.assignmentUpdate.contract &&
                this.assignmentUpdate.contract.id === null) {
                this.assignmentUpdate.contract = this.observation.contract
            }
            if(this.observation.work_order && this.observation.work_order.id &&
                (!this.assignmentUpdate.work_order || this.assignmentUpdate.work_order.id === null)) {
                this.assignmentUpdate.work_order = this.observation.work_order
            }
        }
        if (this.isObserver) {
            this.fetchUsers()
            this.fetchOpenContracts()
            this.fetchContractors()
        }
        if(this.coord && this.coord.length > 0) {
            let geometry = {'point': {'lat': this.coord[0].lat, 'lon': this.coord[0].lng}}
            this.addGeometryToAssignment(geometry)
        }
        if(this.assignmentUpdate.geometry){
            this.userDefinedGeometry = true
        }else{
            this.userDefinedGeometry = false
        }
        if(this.observation) {
            this.editableObservation = this.observation
        } else if(this.assignmentUpdate.observation){
            this.editableObservation = this.assignmentUpdate.observation
        }
        this.initAssignmentEvents()
    },
    watch: {
        contracts: function () {
            if (this.assignmentUpdate.contract) {
                let selected = this.contracts.find(item => item.id === this.assignmentUpdate.contract.id)
                if (selected) {
                    this.assignmentUpdate.contract = selected
                } else {
                    this.contracts.push(this.assignmentUpdate.contract)
                }
            }
        },
        'assignmentUpdate.status': {
            handler: function (value) {
                if (value === this.OPEN) {
                    this.assignee = null
                }
            },
            deep: true
        },
        assignee:  function (value) {
            if (value && this.assignmentUpdate.status === this.OPEN) {
                this.assignmentUpdate.assigned_to = this.users.find(user => user.id === value)
                this.assignmentUpdate.status = this.ASSIGNED
            } else if (value === null) {
                this.assignmentUpdate.assigned_to = null
            }
        },
        'assignmentUpdate.contract': {
            handler: function (value) {
                if (value && this.assignmentUpdate.id < 1) {
                    this.assignmentUpdate.status = 1
                }
                if (!value) {
                    // Contract reset -> reset also order
                    this.assignmentUpdate.work_order = null
                }
                this.fetchContractors()
            },
            deep: true
        },
        'assignmentUpdate.street_address': {
            handler: function (value) {
                if (!this.addressHasChanged && value && this.assignmentUpdate.street_address === value) {
                    this.addressHasChanged = true
                }
            },
        },
        'assignmentUpdate.city': {
            handler: function (value) {
                if (!this.addressHasChanged && value && this.assignmentUpdate.city === value) {
                    this.addressHasChanged = true
                }
            },
        },
        'assignmentUpdate.area_equipment': {
            handler: function() {
                this.initPhotos()
            }
        }
    },
    computed: {

        canCreateObservation() {
            return this.assignmentUpdate.geometry && (this.assignmentUpdate.geometry.line_string || this.assignmentUpdate.geometry.point)
        },

        assigneeInsideOrganization() {
            return this.assignmentUpdate.assigned_to && this.users.find(user => user.id === this.assignmentUpdate.assigned_to.id)
        },

        observationCoordinates() {
            if (this.assignmentUpdate.geometry) {
                if (this.assignmentUpdate.geometry.line_string) {
                    let results = []
                    this.assignmentUpdate.geometry.line_string.points.forEach(point => results.push( {lat: point[1], lng: point[0]}))
                    return results
                }
                if (this.assignmentUpdate.geometry.point) {
                    return [
                        {
                            lat: this.assignmentUpdate.geometry.point.y,
                            lng: this.assignmentUpdate.geometry.point.x
                        },
                    ]
                }
            }
            return null
        },
        commentTopics() {
            return commentTopics
        },
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert')
        return { showAlert }
    },
    methods: {
        initAssignmentEvents() {
            if (this.assignmentUpdate && this.assignmentUpdate.id > 0) {
                const events = []
                if (this.assignmentUpdate) {
                    events.push({
                        id: `${this.assignmentUpdate.id}-created`,
                        workOrderId: this.assignmentUpdate.id,
                        type: 'created',
                        class: this.statusClass(this.OPEN),
                        date: this.assignmentUpdate.created,
                        title: this.getStatusString(this.OPEN)
                    });
                }

                if (this.assignmentUpdate.assigned_date) {
                    events.push({
                        id: `${this.assignmentUpdate.id}-assigned`,
                        workOrderId: this.assignmentUpdate.id,
                        type: 'assigned',
                        class: this.statusClass(this.ASSIGNED),
                        date: this.assignmentUpdate.assigned_date,
                        title: this.getStatusString(this.ASSIGNED)
                    });
                }

                if (this.assignmentUpdate.closing_date) {
                    events.push({
                        id: `${this.assignmentUpdate.id}-closed`,
                        workOrderId: this.assignmentUpdate.id,
                        type: 'closed',
                        class: this.statusClass(this.assignmentUpdate.status),
                        date: this.assignmentUpdate.closing_date,
                        title: this.getStatusString(this.assignmentUpdate.status)
                    });
                }
                this.assignmentEvents = events.sort(
                    (a, b) => new Date(a.date) - new Date(b.date)
                );
            }
        },



        initPhotos() {
            this.assignmentPhotos = this.getAssignmentPhotos(this.assignmentUpdate, this.$refs.comments?.getPhotos())
        },

        initializeFromWorkDefect() {
           if (this.workDefect && (!this.assignmentUpdate.id || this.assignmentUpdate.id < 1)) {
                this.assignmentUpdate.contract = this.workDefect.contract
                this.assignmentUpdate.info = this.workDefect.inspectable_name + ': ' + this.workDefect.description
                this.assignmentUpdate.work_defect = {id: this.workDefect.id }
                if (this.workDefect.location) {
                    this.assignmentUpdate.geometry = { point: {y: this.workDefect.location.y, x: this.workDefect.location.x}}
                }
            }
        },

        setOrder(order){
            this.assignmentUpdate.work_order = order
        },

        triggerFileInput() {
            this.$refs.fileInput?.click();
        },
        handleFileChange(event) {
            Array.from(event.target.files).forEach(file => this.attachments.push(file));
            event.target.value = '';
        },
        removeAttachment(index) {
            this.attachments.splice(index, 1);
        },

        removeOldAttachment(index) {
            this.assignmentUpdate.attachments.splice(index, 1);
        },

        removeDrawnGeometry(){
            this.assignmentUpdate.geometry = null
            this.userDefinedGeometry = false
        },
        addGeometryToAssignment(geometry) {
            this.assignmentUpdate.geometry = geometry
            this.closeDrawGeometryWizard()
            this.userDefinedGeometry = true
        },
        closeDrawGeometryWizard() {
            this.showDrawGeometryWizard = false
        },
        closeForm: function () {
            this.editObservation = false
        },
        copyWorkAssignment() {
            this.showCopyModal = true
        },
        copySuccess: function (data) {
            this.assignmentUpdate = this.newWorkAssignment(data)
            this.showCopyModal = false
            this.$emit('updateWorkAssignments')
            this.loading = false
            this.showAlert(this.$t('work_assignment.copy_successful'), 'success')
        },
        cancelCopying() {
            this.showCopyModal = false
        },

        observationAdded(observation) {
            observation.can_edit = true
            observation.info = this.assignmentUpdate.info
            observation.contract = this.assignmentUpdate.contract
            observation.work_order = this.assignmentUpdate.work_order
            this.editableObservation = observation
            this.editObservation = true
        },

        addObservation() {
            if(!this.viaObservation) {
                this.observationWizard = true
            }
        },
        closeAndUpdateObservations() {
            this.editObservation = false
            this.showAlert(this.$t('observations.observation_added'), 'success')
        },
        fetchUsers: function () {
            this.loading++
            this.users = []
            this.restFetchParams(this.userCompanyUrl, {active: 1}, this.handleResponse)
        },
        handleResponse: function (response) {
            if(response && response.data) {
                this.users = response.data
            }
            if (this.assigneeInsideOrganization) {
                this.assignee = this.assignmentUpdate.assigned_to.id
            }
            this.loading--
        },
        fetchContractors: function () {
            this.loading++
            this.contractors = []
            if (this.assignmentUpdate.contract) {
                this.restFetchParams(this.contractorUrl, {contract: this.assignmentUpdate.contract.id}, this.handleContractorsResponse)
            } else {
                this.restFetch(this.contractorUrl, this.handleContractorsResponse)
            }
        },
        handleContractorsResponse: function (response) {
            this.contractors = response.data
            this.handleAssignedCompany()
            this.loading--
        },
        getAddressCoordinates: function (address) {
            this.addressToCoordinates(address, this.handleAddressCoordinatesSuccess)
        },
        equipmentSelected: function (equipmentId) {
            this.assignmentUpdate.area_equipment = {id: equipmentId}
            this.assignmentPhotos = this.getAssignmentPhotos(this.assignmentUpdate, this.$refs.comments?.getPhotos())
        },
        handleAddressCoordinatesSuccess: function (response) {
            this.assignmentUpdate.position = response
            this.submit();
        },
        handleAssignedCompany() {
            if(this.assignmentUpdate.assigned_company && this.assignmentUpdate.assigned_company.id) {
                if(this.contractors && this.contractors.length > 0) {
                    let foundContractor = this.contractors.some((contractor) => contractor.id === this.assignmentUpdate.assigned_company.id)
                    if(!foundContractor) {
                        this.contractors.push(this.assignmentUpdate.assigned_company)
                    }
                } else {
                    this.contractors.push(this.assignmentUpdate.assigned_company)
                }
            }
        },
        preSubmit: function () {
            if (this.addressHasChanged && this.assignmentUpdate.street_address && this.assignmentUpdate.city) {
                const address = this.assignmentUpdate.street_address + ", " + this.assignmentUpdate.city
                this.getAddressCoordinates(address)
            } else {
                this.submit()
            }
        },
        submit: function () {
            let jsonAssignment = this.setDefaultValues(this.assignmentUpdate)
            if (jsonAssignment) {
                this.loading++
                if (jsonAssignment.id < 1) {
                    this.restAdd(this.workAssignmentUrl, jsonAssignment, this.success, this.fail)
                } else {
                    this.restUpdate(this.workAssignmentUrl, jsonAssignment, this.success, this.fail)
                }
            }
        },
        setDefaultValues: function (assignment) {
            assignment = this.jsonToWorkAssignment(assignment)
            if (assignment.area_defect) {
                assignment.area_defect = {id: assignment.area_defect.id}
            } else if (this.vehicleDefect) {
                assignment.vehicle_defect = {id: this.vehicleDefect.id}
            } else if (this.workDefect) {
                assignment.work_defect = {id: this.workDefect.id}
            } else if (this.observation) {
                assignment.observation = {id: this.observation.id}
            } else if (assignment.observation && assignment.observation.id) {
                assignment.observation = {id: assignment.observation.id}
            }else if (this.workPermission) {
                assignment.special_work_permission = {id: this.workPermission.id}
            }
            if (assignment.area_equipment && assignment.area_equipment.id) {
                assignment.area_equipment = {id: assignment.area_equipment.id}
            } else {
                assignment.area_equipment = undefined;
            }
            if (assignment.contract && assignment.contract.id) {
                assignment.contract = {id: assignment.contract.id}
            } else {
                assignment.contract = undefined
            }
            if (assignment.work_order && assignment.work_order.id) {
                assignment.work_order = {id: assignment.work_order.id}
            } else {
                assignment.work_order = undefined
            }
            if (!assignment.status) {
                this.showAlert(this.$t('work_assignment.select_status'))
                return undefined
            }
            if (this.assignee !== -1 && this.assignee) {
                assignment.assigned_to = {id: this.assignee}
            }
            if (this.assignee === null) {
                assignment.assigned_to = null
            }
            if ((assignment.status === this.ASSIGNED || assignment.status === this.IN_PROGRESS) &&
                !assignment.assigned_to) {
                this.showAlert(this.$t('work_assignment.error_state'))
                return undefined
            }
            if (assignment.assigned_company && assignment.assigned_company.id) {
                assignment.assigned_company = {id: assignment.assigned_company.id}
            } else {
                assignment.assigned_company = undefined
            }
            // Polygon geometry needs special handling if not edited: rings -> points
            if (assignment.geometry && assignment.geometry.polygon && assignment.geometry.polygon.rings) {
                assignment.geometry.polygon = this.convertPolygon(assignment.geometry.polygon)
            }
            return assignment
        },

        convertPolygon: function (polygon) {
            if (polygon.rings && polygon.rings.length > 0) {
                    let original = polygon.rings[0] // No support for many rings for now
                    let mapped = original.map(item => [item[0], item[1]])
                    mapped.push(mapped[0])
                    return {points:  mapped}
            }
            return polygon
        },


        success: function (workAssignment) {
            // Set id for new assignment
            if (!this.assignmentUpdate.id || this.assignmentUpdate.id < 0) {
                this.assignmentUpdate.id = workAssignment.id
            }
            let attachments = this.getValidAttachments(this.attachments)
            if (attachments.length > 0) {
                this.uploadAttachment(attachments)
            } else {
                this.loading--
                this.$emit('closeAndUpdate', this.assignmentUpdate)
            }
        },
        fail: function () {
            this.showAlert(this.$t('common.alert_update_failed'))
            this.loading--
        },
        canAssign: function () {
            return this.isObserver
        },
        uploadAttachment: function (attachments) {
            if (attachments.length > 0) {
                this.loading++
                let formData = new FormData()
                attachments.forEach(file => {
                    if (file !== undefined && file !== null) {
                        formData.append('attachments[]', file)
                    }
                })
                this.restPostWithFile(this.workAssignmentAttachmentUrl + this.assignmentUpdate.id, {}, formData,
                    ()=>{
                        this.loading--
                        this.$emit('closeAndUpdate', this.assignmentUpdate)
                    }, this.fail
                )
            }
        },
        handleContractResults: function (response) {
            this.loading--
            if (response != null) {
                this.contracts = response.data
            }
        },
        downloadAttachment: function (item) {
            this.loading++
            this.restFetch(this.workAssignmentAttachmentUrl + this.assignmentUpdate.id + '/' + item.id,
                response => {
                    this.saveFile(item.file_original_name, response.data)
                    this.loading--
                }, () => {
                    this.loading--
                })
        },
        scrollTop: function () {
            this.$nextTick(function () {
                this.$refs.container.scrollTop = 0
            })
        },
        updateStartDate: function (event) {
            if (event.target.value) {
                this.assignmentUpdate.planned_start = new Date(event.target.value)
            } else {
                this.assignmentUpdate.planned_start = undefined
            }
        },

        updateEndDate: function (event) {
            if (event.target.value) {
                // We need to add 23 h 59 minutes to the date to cover the whole day
                var endDate = new Date(event.target.value)
                endDate.setHours(23)
                endDate.setMinutes(59)
                endDate.setSeconds(59)
                this.assignmentUpdate.planned_ready = endDate
            } else {
                this.assignmentUpdate.planned_ready = undefined
            }
        }
    }
}
</script>
